import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { KEY_RECOIL, canShowCountBeatEvents, canShowDurationEvents } from '@/shared/config';
import { useEventsInfoRhythmEvents } from '@/entities/event';
import {
  selectActiveTab,
  selectEventChanges,
  selectIsActiveTab,
  selectGroupEventChanges,
  useReportStore,
} from '@/entities/report';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';

/**
 * Supplies the rhythm events tab's left column with the active event type, its click handler and the ECG bookmark counts, plus the review-progress and unapplied-change lookups its menu renderers need. Binds every report-store setter to the TAB_2 key, resets filter, pagination, selection, column, ECG object and jump state when the reviewer switches event type.
 *
 * @returns An object carrying activeButton, onClickTab, ecgBookmarksLength, ecgBookmarkDeleteIds, eventsInfo, hasGroupTypeChangeTabInfos, hasUnappliedChangeTabInfos, groupEventChanges and eventChanges.
 */
const useLeftColumn = () => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setActiveButtonAction = useReportStore((s) => s.actions.setActiveTab);
  /**
   * Sets which event type is active in the rhythm events left column. Forwards the value to the report store's setActiveTab action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The event type to make active.
   * @returns The result of the underlying store action call.
   */
  const setActiveButton = (v: Parameters<typeof setActiveButtonAction>[1]) =>
    setActiveButtonAction(keyRecoil, v);
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Stores the date currently selected on the rhythm events tab. Forwards the value to the report store's setSelectedDateValue action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The selected date, or null to clear it.
   * @returns The result of the underlying store action call.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Records whether the rhythm events tab currently has no data to display. Forwards the flag to the report store's setIsEmptyData action with the TAB_2 keyRecoil already bound.
   *
   * @param v - True when the tab has no data to display.
   * @returns The result of the underlying store action call.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the single strip currently active on the rhythm events tab. Forwards the value to the report store's setSelectedStrip action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The strip to make active, or null to clear the selection.
   * @returns The result of the underlying store action call.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const setSelectedColumnInfoAction = useReportStore((s) => s.actions.setSelectedColumnInfo);
  /**
   * Stores which heart-rate chart columns are selected on the rhythm events tab. Forwards the value to the report store's setSelectedColumnInfo action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The selected column info entries, emptied when the event type changes.
   * @returns The result of the underlying store action call.
   */
  const setSelectedColumnInfo = (v: Parameters<typeof setSelectedColumnInfoAction>[1]) =>
    setSelectedColumnInfoAction(keyRecoil, v);
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Stores the events picked inside the selected heart-rate columns on the rhythm events tab. Forwards the value to the report store's setSelectedEventInfo action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The selected event info entries, emptied when the event type changes.
   * @returns The result of the underlying store action call.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const setPrevColumnInfoAction = useReportStore((s) => s.actions.setPrevColumnInfo);
  /**
   * Stores the heart-rate chart column preceding the current selection on the rhythm events tab. Forwards the value to the report store's setPrevColumnInfo action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The previous column info entries, emptied when the event type changes.
   * @returns The result of the underlying store action call.
   */
  const setPrevColumnInfo = (v: Parameters<typeof setPrevColumnInfoAction>[1]) =>
    setPrevColumnInfoAction(keyRecoil, v);
  const setNextColumnInfoAction = useReportStore((s) => s.actions.setNextColumnInfo);
  /**
   * Stores the heart-rate chart column that follows the current selection on the rhythm events tab. Forwards the value to the report store's setNextColumnInfo action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The next column info entries, emptied when the event type changes.
   * @returns The result of the underlying store action call.
   */
  const setNextColumnInfo = (v: Parameters<typeof setNextColumnInfoAction>[1]) =>
    setNextColumnInfoAction(keyRecoil, v);
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Marks the rhythm events heart-rate chart as loading or settled. Forwards the flag to the report store's setIsLoadingHrChart action with the TAB_2 keyRecoil already bound.
   *
   * @param v - True while the heart-rate chart is loading.
   * @returns The result of the underlying store action call.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const eventsInfo = useEventsInfoRhythmEvents();
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Updates the page index of the rhythm events strip list. Forwards the value, which may be an updater receiving the current index, to the report store's setPageIndex action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The next page index, or an updater function receiving the current one.
   * @returns The result of the underlying store action call.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Stores the multi-strip selection of the rhythm events tab. Forwards the value to the report store's setSelectedMultipleStrip action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The selected strips, emptied when the event type changes or the tab is re-activated.
   * @returns The result of the underlying store action call.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Clears the control modal flags of the rhythm events tab. Calls the report store's resetControlModal action with the TAB_2 keyRecoil already applied.
   *
   * @returns The result of the underlying store action call.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Marks the rhythm events strip list as loading or settled. Forwards the flag to the report store's setIsLoadingStrip action with the TAB_2 keyRecoil already bound.
   *
   * @param v - True while the strip list is loading.
   * @returns The result of the underlying store action call.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Replaces the ECG object held for the rhythm events tab. Forwards the value to the report store's setEcgObject action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The ECG object to store, emptied when the reviewer switches event type.
   * @returns The result of the underlying store action call.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the rhythm events viewer should jump to. Forwards the value to the report store's setJumpDate action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The target date, or undefined to clear the pending jump.
   * @returns The result of the underlying store action call.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Sets the time of day the rhythm events viewer should jump to. Forwards the value to the report store's setJumpTime action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The target time, or undefined to clear the pending jump.
   * @returns The result of the underlying store action call.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets the portion within the day the rhythm events viewer should jump to. Forwards the value to the report store's setJumpPortion action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The target portion index, reset to zero when the event type changes.
   * @returns The result of the underlying store action call.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const ecgBookmarkDeleteIds = useReportStore((s) => s.ecgBookmarkDeleteIds);
  const setActionSnapshot = useReportStore((s) => s.actions.setActionSnapshot);
  const setEventFilterAction = useReportStore((s) => s.actions.setEventFilter);
  /**
   * Updates the event filter of the rhythm events tab. Forwards the value, which may be an updater receiving the current filter, to the report store's setEventFilter action with the TAB_2 keyRecoil already bound.
   *
   * @param v - The next event filter, or an updater function receiving the current one.
   * @returns The result of the underlying store action call.
   */
  const setEventFilter = (v: Parameters<typeof setEventFilterAction>[1]) =>
    setEventFilterAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));

  const ecgBookmarksLength = ecgBookmarks.length;

  const previoustTab = useRef(activeButton);

  /**
   * Switches the rhythm events tab to another event type and clears everything tied to the previous one. Aborts in-flight API calls, resets the control modal, keeps the sort order only when the new and previous types stay within the same count-beat or duration family, rewinds pagination, clears the date, strip, column and jump state, flags the strip list and heart-rate chart as loading and bumps the undo snapshot counter.
   *
   * @param tab - The event type to activate; ignored when it already is the active one.
   * @returns Nothing.
   */
  const onClickTab = (tab: any) => {
    if (activeButton === tab) {
      return;
    }
    cancelAllAPI();
    // resetEcgViewerConfigStore(););
    resetControlModal();
    setEventFilter((prev: any) => {
      if (
        prev.sortOrder &&
        canShowCountBeatEvents.includes(tab) &&
        !canShowDurationEvents.includes(previoustTab.current as any)
      ) {
        return prev;
      }
      if (
        prev.sortOrder &&
        canShowDurationEvents.includes(tab) &&
        !canShowCountBeatEvents.includes(previoustTab.current as any)
      ) {
        return prev;
      }
      return { ...prev, sortOrder: undefined, sortBy: undefined };
    });
    setPageIndex((prev: any) => (prev.index === 0 ? prev : { index: 0 }));
    setSelectedDateValue(null);
    setActiveButton(tab);
    setIsEmptyData(false);
    setSelectedColumnInfo([]);
    setSelectedEventInfo([]);
    setPrevColumnInfo([]);
    setNextColumnInfo([]);
    setSelectedStrip(null);
    setSelectedMultipleStrip([]);
    setIsLoadingStrip(true);
    setIsLoadingHrChart(true);
    setEcgObject({});
    setJumpDate(undefined);
    setJumpTime(undefined);
    setJumpPortion(0);
    setActionSnapshot(1);
    previoustTab.current = tab;
  };

  const hasGroupTypeChangeTabInfos: any = useMemo(() => {
    const result: any = {};
    _.forEach(groupEventChanges, (groupEvent: any) => {
      if (!result[groupEvent.groupEventType]) {
        result[groupEvent.groupEventType] = true;
      }
    });
    return result;
  }, [groupEventChanges]);

  const hasUnappliedChangeTabInfos: any = useMemo(() => {
    const result: any = {};
    _.forEach(eventChanges, (event: any) => {
      if (!result[event.oldType]) {
        result[event.oldType] = true;
      }
    });
    return result;
  }, [eventChanges]);

  useEffect(() => {
    if (isActiveTab) {
      setTimeout(() => {
        setActionSnapshot(1);
      }, 200);
    }
    setSelectedMultipleStrip([]);
  }, [isActiveTab]);

  return {
    activeButton,
    onClickTab,
    ecgBookmarksLength,
    ecgBookmarkDeleteIds,
    eventsInfo,
    hasGroupTypeChangeTabInfos,
    hasUnappliedChangeTabInfos,
    groupEventChanges,
    eventChanges,
  };
};

export default useLeftColumn;
